/**
 * Demo Data Service
 *
 * Provides curated, sales-presentation-ready demo data for MarTech Expo 2026.
 * All demo data shows optimal performance with clear ROI stories.
 *
 * Demo data characteristics:
 * - Strong upward trends (showing improvement over time)
 * - ROAS: 3.0x - 6.0x (excellent returns)
 * - CTR: 2.5% - 5.0% (above industry average)
 * - CPL decreasing trend (showing optimization value)
 *
 * INDUSTRY-AGNOSTIC: Uses generic marketing terminology
 * - Leads (secondary metric) - was "quotes"
 * - Conversions (primary metric) - was "policies"
 */

import { aggregateMetrics } from '@/utils/campaignAggregator';
import { todayDataDate } from './dataDate';
import type {
  CampaignMetricsData,
  ChannelPerformance,
  TrendData,
  BrokerPerformance,
} from './mediaIntelligenceService'

// ============================================================================
// MOVED TO SEVEN MODULES — re-exported so no call site changes
// ============================================================================
//
// The width of each re-export is chosen, not defaulted: it reproduces the
// public surface this file had before the split, name for name. Nine helpers
// that were module-private (PLATFORMS, PlatformKey, PERFORMANCE_TIERS,
// PLATFORM_MODIFIERS, generateDateRange, generateRealisticTrendingValue,
// DEMO_CAMPAIGNS, DEMO_SALES_AGENTS, generateDemoCampaigns) had to gain
// `export` so their new siblings could read them. A star here would publish all
// nine; a named list keeps them internal to the demo layer, which is where the
// generator's private detail belongs.
export type {
  DemoUserCredentials, PerformanceTierConfig, PlatformDistribution, KPIExpectations, DemoConfig,
} from './demoDataConfig';
export { DEMO_DATE_ANCHOR, DEMO_DATA_RANGE_DAYS, DEMO_CONFIG } from './demoDataConfig';
export { generateTrendingValue, getTierROAS, getTierCTR } from './demoDataTrending';
export type { DemoCampaign } from './demoDataFixtures';
export { getTierConfig, generateCampaigns, getDemoCampaignsFromClassified } from './demoDataFixtures';
// demoDataHierarchy exports one name and it was private: nothing to re-export.
export * from './demoDataFunnel';
export * from './demoDataMmm';
export * from './demoDataStatic';

// And imported for this module's OWN use — a re-export hands names to callers,
// it does not put them in scope here. Six of these eight are names the split
// had to promote from private; they are read here and published nowhere.
import {
  DEMO_CONFIG, DEMO_DATE_ANCHOR, DEMO_DATA_RANGE_DAYS,
  PLATFORMS, PLATFORM_MODIFIERS, type PlatformKey,
} from './demoDataConfig';
import {
  generateDateRange, generateRealisticTrendingValue, generateTrendingValue,
  getTierROAS, getTierCTR,
} from './demoDataTrending';
import {
  DEMO_SALES_AGENTS, getTierConfig, generateCampaigns, getDemoCampaignsFromClassified,
} from './demoDataFixtures';
import { generateDemoCampaigns } from './demoDataHierarchy';
import { getDemoFunnelData } from './demoDataFunnel';
import { getDemoMMMData } from './demoDataMmm';
import {
  USE_STATIC_DEMO_DATA, loadStaticDemoData, clearStaticDataCache, hasStaticDemoData,
} from './demoDataStatic';
import { classifyCampaignByFunnel } from '@/config/funnelConfig';




// ============================================================================
// DATA GENERATION FUNCTIONS
// ============================================================================

// ============================================================================
// DATA GENERATION FUNCTIONS - Industry-agnostic
// ============================================================================

/**
 * Get demo campaign metrics with realistic performance variation
 * Returns data matching CampaignMetricsData interface
 *
 * INDUSTRY-AGNOSTIC: Uses generic marketing terminology
 * - quotes (legacy) = leads (new generic term)
 * - policies (legacy) = conversions (new generic term)
 */
// ============================================================================
// MODULE-LEVEL CACHE — ensures deterministic data across all consumers
// ============================================================================
// Demo data is generated ONCE on first access, then cached. This guarantees
// that the campaigns page, chat context, and MMM views all see identical
// numbers. Without caching, Math.random() in the generators produced
// different values on every call, causing data mismatches.

let _cachedMetrics: CampaignMetricsData | null = null
let _cachedChannels: ChannelPerformance[] | null = null
let _cachedTrends: TrendData | null = null
let _cachedBrokers: BrokerPerformance[] | null = null

/** Clear all cached demo data (forces regeneration on next access). */
export function clearDemoDataCache(): void {
  _cachedMetrics = null
  _cachedChannels = null
  _cachedTrends = null
  _cachedBrokers = null
}

/**
 * Period scale factors relative to the base 90-day (quarter) dataset.
 * Volume metrics (reach, leads, conversions, spend, revenue) are scaled;
 * Rate metrics (ROAS, CTR, CPL, conversionRate) remain constant.
 */
const PERIOD_SCALE: Record<string, number> = {
  week: 7 / 90,
  month: 30 / 90,
  quarter: 1,
}

/**
 * Scale volume metrics by period while keeping rate metrics unchanged.
 */
export function scaleMetricsByPeriod(base: CampaignMetricsData, period: string): CampaignMetricsData {
  const scale = PERIOD_SCALE[period] ?? 1
  if (scale === 1) return base
  return {
    ...base,
    impressions: Math.round(base.impressions * scale),
    conversions: Math.round(base.conversions * scale),
    monthlyRevenue: base.monthlyRevenue ? Math.round(base.monthlyRevenue * scale) : undefined,
    monthlySpend: base.monthlySpend ? Math.round(base.monthlySpend * scale) : undefined,
    // Rates stay constant
    conversionRate: base.conversionRate,
    roas: base.roas,
    ctr: base.ctr,
    // Growth percentages stay constant
  }
}

/**
 * Scale channel performance volume metrics by period.
 */
export function scaleChannelsByPeriod(base: ChannelPerformance[], period: string): ChannelPerformance[] {
  const scale = PERIOD_SCALE[period] ?? 1
  if (scale === 1) return base
  return base.map((ch) => ({
    ...ch,
    impressions: Math.round(ch.impressions * scale),
    clicks: Math.round(ch.clicks * scale),
    reach: Math.round((ch.reach ?? 0) * scale),
    // A rate, so it does NOT scale with volume the way the counts above do.
    ctr: ch.ctr ?? 0,
    conversions: Math.round(ch.conversions * scale),
    cost: Math.round(ch.cost * scale),
    revenue: Math.round(ch.revenue * scale),
    // Rates stay constant
    cpc: ch.cpc,
    conversionRate: ch.conversionRate,
    roas: ch.roas,
    // Scale nested campaigns/adSets
    campaigns: ch.campaigns?.map((c) => ({
      ...c,
      impressions: Math.round(c.impressions * scale),
      clicks: Math.round(c.clicks * scale),
      conversions: Math.round(c.conversions * scale),
      cost: Math.round(c.cost * scale),
      revenue: Math.round(c.revenue * scale),
      adSets: c.adSets?.map((a) => ({
        ...a,
        impressions: Math.round(a.impressions * scale),
        clicks: Math.round(a.clicks * scale),
        conversions: Math.round(a.conversions * scale),
        cost: Math.round(a.cost * scale),
        revenue: Math.round(a.revenue * scale),
        ads: a.ads?.map((ad) => ({
          ...ad,
          impressions: Math.round(ad.impressions * scale),
          clicks: Math.round(ad.clicks * scale),
          conversions: Math.round(ad.conversions * scale),
          cost: Math.round(ad.cost * scale),
          revenue: Math.round(ad.revenue * scale),
        })),
      })),
    })),
  }))
}

export function getDemoCampaignMetrics(period: string = 'quarter'): CampaignMetricsData {
  if (!_cachedMetrics) {
    const base = generateDemoCampaignMetricsInternal()

    // Reconcile aggregate CPL, spend, and leads against channel data so that
    // HeroMetrics (sum of channel cost / leads) matches metrics.cpl exactly.
    // Without this, the aggregate generator and channel generator produce
    // independent numbers and the dashboard shows an arithmetic impossibility
    // where the overall CPL appears higher (or lower) than every per-channel CPL.
    // Any period works because cost & leads scale proportionally.
    const channels = getDemoChannelPerformance('quarter')
    const channelCost = channels.reduce((sum, ch) => sum + (ch.cost ?? 0), 0)
    const channelClicks = channels.reduce((sum, ch) => sum + (ch.clicks ?? 0), 0)

    if (channelCost > 0 && channelClicks > 0) {
      _cachedMetrics = {
        ...base,
        monthlySpend: channelCost,
      }
    } else {
      _cachedMetrics = base
    }
  }
  return scaleMetricsByPeriod(_cachedMetrics, period)
}

/** Internal generator — called once, result cached. */
function generateDemoCampaignMetricsInternal(): CampaignMetricsData {
  const tierWeights = { tier1: 0.3, tier2: 0.4, tier3: 0.2, tier4: 0.1 }

  // Calculate weighted averages based on performance profiles
  const avgROAS =
    tierWeights.tier1 *
      ((DEMO_CONFIG.performanceProfiles.tier1.roasRange[0] +
        DEMO_CONFIG.performanceProfiles.tier1.roasRange[1]) /
        2) +
    tierWeights.tier2 *
      ((DEMO_CONFIG.performanceProfiles.tier2.roasRange[0] +
        DEMO_CONFIG.performanceProfiles.tier2.roasRange[1]) /
        2) +
    tierWeights.tier3 *
      ((DEMO_CONFIG.performanceProfiles.tier3.roasRange[0] +
        DEMO_CONFIG.performanceProfiles.tier3.roasRange[1]) /
        2) +
    tierWeights.tier4 *
      ((DEMO_CONFIG.performanceProfiles.tier4.roasRange[0] +
        DEMO_CONFIG.performanceProfiles.tier4.roasRange[1]) /
        2)

  const avgCPL =
    tierWeights.tier1 *
      ((DEMO_CONFIG.performanceProfiles.tier1.cplRange[0] +
        DEMO_CONFIG.performanceProfiles.tier1.cplRange[1]) /
        2) +
    tierWeights.tier2 *
      ((DEMO_CONFIG.performanceProfiles.tier2.cplRange[0] +
        DEMO_CONFIG.performanceProfiles.tier2.cplRange[1]) /
        2) +
    tierWeights.tier3 *
      ((DEMO_CONFIG.performanceProfiles.tier3.cplRange[0] +
        DEMO_CONFIG.performanceProfiles.tier3.cplRange[1]) /
        2) +
    tierWeights.tier4 *
      ((DEMO_CONFIG.performanceProfiles.tier4.cplRange[0] +
        DEMO_CONFIG.performanceProfiles.tier4.cplRange[1]) /
        2)

  // Generate hero metrics based on KPI expectations
  const monthlySpend = DEMO_CONFIG.totalMonthlyBudget * (0.9 + Math.random() * 0.2)
  const monthlyRevenue = monthlySpend * avgROAS

  // Calculate conversions based on ROAS and avg conversion value
  const avgConversionValue = 6000 // Lead conversion value
  const totalConversions = Math.floor(monthlyRevenue / avgConversionValue)
  // CampaignMetricsData.leads — totalSignups, a DIFFERENT field from the
  // PerformanceMetrics.leads deleted in this commit (that one held clicks).
  const totalLeads = Math.floor(totalConversions * 2.5)

  // Calculate reach based on budget - more consistent multiplier
  const reachMultiplier = 2.0 // Lead gen reach multiplier
  const totalImpressions = Math.floor(monthlySpend * reachMultiplier * (2.2 + Math.random() * 0.6)) // Less variation: 2.2-2.8x

  const ctr = DEMO_CONFIG.kpiExpectations.targetCTR + (Math.random() - 0.5) * 1.0 // Less variation: +/-0.5%

  return {
    impressions: totalImpressions,
    impressionsGrowth: 8.0 + Math.random() * 8, // Moderate growth: 8-16% (no fake spikes)
    // Derived from the two beside it, not generated separately — demo numbers a
    // reader can check against each other are the only kind worth showing.
    clicks: Math.round((totalImpressions * ctr) / 100),
    ctr,
    leads: totalLeads,
    ctrGrowth: 4.0 + Math.random() * 6, // Moderate growth: 4-10%
    leadsGrowth: 12.0 + Math.random() * 10, // Moderate growth: 12-22% (was 18-38%)
    conversions: totalConversions,
    conversionsGrowth: 15.0 + Math.random() * 10, // Moderate growth: 15-25% (was 22-40%)
    // CPL is reconciled with channel data in getDemoCampaignMetrics() below
    // to ensure sum(channels.cost)/sum(channels.leads) === metrics.cpl.
    cpl: avgCPL + (Math.random() - 0.5) * avgCPL * 0.15,
    cplChange: -4.0 - Math.random() * 6, // Moderate improvement: -4% to -10%
    conversionRate: DEMO_CONFIG.kpiExpectations.conversionRate + (Math.random() - 0.5) * 0.5, // Less variation
    monthlyRevenue,
    monthlySpend,
    roas: avgROAS + (Math.random() - 0.5) * 0.3, // Less variation: +/-0.15
  }
}


/**
 * Get demo channel performance by platform
 * INDUSTRY-AGNOSTIC: Uses leads and conversions terminology
 * Includes platform-specific modifiers and realistic performance variation
 */
export function getDemoChannelPerformance(period: string = 'quarter'): ChannelPerformance[] {
  if (!_cachedChannels) {
    _cachedChannels = generateDemoChannelPerformanceInternal()
  }
  return scaleChannelsByPeriod(_cachedChannels, period)
}

/** Internal generator — called once, result cached. */
function generateDemoChannelPerformanceInternal(): ChannelPerformance[] {
  const platformMix = DEMO_CONFIG.platformMix

  return Object.entries(PLATFORMS).map(([key, platform]) => {
    const platformKey = key as PlatformKey
    const modifier = PLATFORM_MODIFIERS[platformKey]
    const spendPercentage = platformMix[platformKey] || platform.spendPercentage

    const baseSpend = DEMO_CONFIG.totalMonthlyBudget * spendPercentage * modifier.costMultiplier
    const baseClicks = baseSpend / 12 // ~฿12 CPC
    const baseConversions =
      baseClicks * (DEMO_CONFIG.kpiExpectations.conversionRate / 100) * modifier.roasMultiplier

    // Calculate ROAS with platform modifiers
    const roas =
      platform.targetROAS *
        modifier.roasMultiplier *
        (DEMO_CONFIG.kpiExpectations.targetROAS / 4.0) +
      (Math.random() - 0.5)

    // Calculate CTR with platform modifiers
    const baseCTR =
      DEMO_CONFIG.kpiExpectations.targetCTR * modifier.ctrMultiplier +
      (Math.random() - 0.5) * 1.0
    const impressions = Math.floor(baseClicks / (baseCTR / 100))

    // Generate campaigns for this channel
    const campaigns = generateDemoCampaigns(key)

    // Aggregate metrics from campaigns
    const campaignsMetrics = campaigns.map((c) => ({ metrics: c }))
    const aggregatedMetrics = aggregateMetrics(campaignsMetrics)

    // Hoisted so the CTR below is derived from the SAME two numbers this
    // channel reports, rather than being generated independently of them.
    const chImpressions = aggregatedMetrics.impressions || Math.floor(impressions + Math.random() * 500000)
    const chClicks = aggregatedMetrics.clicks || Math.floor(baseClicks + Math.random() * 20000)

    return {
      id: key,
      key,
      name: platform.name,
      icon: platform.icon,
      // Use aggregated metrics from campaigns instead of calculated
      impressions: chImpressions,
      clicks: chClicks,
      reach: Math.floor(baseClicks * 3 + Math.random() * 100000),
      // Was a COUNT (baseClicks * 0.4) in a field that held a RATE — the
      // confusion ran both ways. Derived from this channel's own two numbers so
      // a reader checking clicks ÷ impressions gets the rate shown.
      ctr: chImpressions > 0 ? (chClicks / chImpressions) * 100 : 0,
      conversions: aggregatedMetrics.conversions || Math.floor(baseConversions + Math.random() * 30),
      cost: aggregatedMetrics.cost || baseSpend + Math.random() * 100000,
      revenue: aggregatedMetrics.revenue || (baseSpend * roas),
      // CPL must be derived from the same cost & leads that are displayed,
      // otherwise sum(channels.cost)/sum(channels.leads) ≠ average of channel CPLs.
      cpl: aggregatedMetrics.cost > 0 && aggregatedMetrics.clicks > 0
        ? aggregatedMetrics.cost / aggregatedMetrics.clicks
        : baseSpend / (baseConversions * 2),
      // CPC at channel level = cost / clicks (consistent with displayed values).
      cpc: aggregatedMetrics.clicks > 0
        ? aggregatedMetrics.cost / aggregatedMetrics.clicks
        : baseSpend / baseClicks,
      conversionRate: DEMO_CONFIG.kpiExpectations.conversionRate + (Math.random() - 0.5) * 2.0,
      roas,
      // Remove objective and platform from channel level - now at campaign level
      campaigns,
    }
  })
}

/**
 * Get demo trend data for charts (last 90 days)
 * INDUSTRY-AGNOSTIC: Uses conversions terminology
 * Shows realistic mixed trends based on performance tier distribution
 */
export function getDemoTrendData(period: string = 'quarter'): TrendData {
  if (!_cachedTrends) {
    _cachedTrends = generateDemoTrendDataInternal()
  }
  // For shorter periods, show fewer data points from the end of the series
  const base = _cachedTrends
  if (period === 'week') {
    // Last ~1/13 of the data points for a week view
    const sliceStart = Math.floor(0.92 * base.clicks.length)
    return {
      clicks: base.clicks.slice(sliceStart),
      conversions: base.conversions.slice(sliceStart),
      costPerConversion: base.costPerConversion.slice(sliceStart),
      spend: base.spend.slice(sliceStart),
      // Sliced with them, or position i would name a different day than it holds.
      buckets: base.buckets.slice(sliceStart),
    }
  }
  if (period === 'month') {
    // Last ~1/3 of the data points for a month view
    const sliceStart = Math.floor(0.67 * base.clicks.length)
    return {
      clicks: base.clicks.slice(sliceStart),
      conversions: base.conversions.slice(sliceStart),
      costPerConversion: base.costPerConversion.slice(sliceStart),
      spend: base.spend.slice(sliceStart),
      buckets: base.buckets.slice(sliceStart),
    }
  }
  return base
}

/** Internal generator — called once, result cached. */
function generateDemoTrendDataInternal(): TrendData {
  const dates = generateDateRange()
  const totalDays = dates.length

  // Base values reflect realistic performance variation
  const baseValues = {
    conversions: 3.5, // Average 3-4 conversions per day
    clicks: 8.0, // Average 8 clicks per day (scaled x10 below)
    costPerConversion: 1350, // ฿ per conversion
    spend: 27000, // ~฿27K per day (750K / 28 days)
  }

  // Mix of trend directions based on tier distribution
  // 70% upward/flat (tier1-3), 30% downward (tier4)
  return {
    clicks: dates.map((_, index) => {
      // Mixed trends: mostly improving with some volatility
      const tier = Math.random() < 0.7 ? 'tier2' : 'tier4'
      return Math.floor(
        generateRealisticTrendingValue(baseValues.clicks * 10, index, totalDays, tier)
      )
    }),
    conversions: dates.map((_, index) => {
      // Conversions show better performance overall
      const tier = Math.random() < 0.75 ? 'tier1' : 'tier3'
      return Math.floor(
        generateRealisticTrendingValue(baseValues.conversions * 10, index, totalDays, tier)
      )
    }),
    costPerConversion: dates.map((_, index) => {
      // Should trend down (improving) for most campaigns
      const tier = Math.random() < 0.8 ? 'tier1' : 'tier3'
      // Invert the trend — downward is good
      const value = generateRealisticTrendingValue(baseValues.costPerConversion, index, totalDays, tier)
      return Math.floor(value * (1 - (index / totalDays) * 0.15)) // Additional downward trend
    }),
    spend: dates.map((_, index) => {
      // Spend shows growth (scaling up successful campaigns)
      const tier = Math.random() < 0.6 ? 'tier1' : 'tier2'
      return Math.floor(generateRealisticTrendingValue(baseValues.spend, index, totalDays, tier))
    }),
    // One bucket per generated day, named by that day. The live series buckets
    // by week and this one by day, which is exactly why every bucket carries
    // its own span rather than letting the reader assume one.
    buckets: dates.map((d) => {
      const day = d.toISOString().slice(0, 10)
      return { start: day, end: day, includesToday: day === todayDataDate() }
    }),
  }
}

/**
 * Get demo broker/agent performance
 * INDUSTRY-AGNOSTIC: Uses conversions instead of policies sold
 */
export function getDemoBrokerPerformance(period: string = 'quarter'): BrokerPerformance[] {
  if (!_cachedBrokers) {
    _cachedBrokers = generateDemoBrokerPerformanceInternal()
  }
  const scale = PERIOD_SCALE[period] ?? 1
  if (scale === 1) return _cachedBrokers
  return _cachedBrokers.map((b) => ({
    ...b,
    conversions: Math.round(b.conversions * scale),
    spend: Math.round(b.spend * scale),
    commission: Math.round(b.commission * scale),
    // roi and commissionRate are rates — scaling the period does not move them.
  }))
}

/** Internal generator — called once, result cached. */
function generateDemoBrokerPerformanceInternal(): BrokerPerformance[] {
  // Demo mode is allowed to be fictional — that is its job. What it may not do
  // is carry fields media_brokers has no column for, because the demo shape and
  // the live shape are the same type and the live one must stay honest.
  return DEMO_SALES_AGENTS.map((agent, idx) => {
    const seniority = idx < 3 ? 'senior' : idx < 7 ? 'intermediate' : 'junior'
    const baseConversions = { senior: 58, intermediate: 34, junior: 19 }[seniority] ?? 34
    const conversions = Math.floor(baseConversions + (Math.random() * 14 - 7))
    const spend = Math.round(conversions * (1600 + Math.random() * 900))
    const commissionRate = Number((5 + Math.random() * 3).toFixed(2))
    return {
      id: agent.id,
      name: agent.name,
      type: idx % 3 === 0 ? 'freelance' : 'agency',
      conversions,
      spend,
      roi: Number((1.8 + Math.random() * 1.6).toFixed(2)),
      // Agent rows now carry the window they cover; demo takes the same
      // rolling 30 days as the rest of the demo data.
      periodStart: new Date(Date.now() - 29 * 86_400_000).toISOString().slice(0, 10),
      periodEnd: new Date().toISOString().slice(0, 10),
      commissionRate,
      commission: Math.round((spend * commissionRate) / 100),
    }
  })
}

/**
 * Get demo platform accounts (simulated connections)
 */
export function getDemoPlatformAccounts() {
  return [
    {
      id: 'demo-account-facebook',
      platform: 'facebook',
      accountName: 'Demo Facebook Account',
      accountType: 'demo_data' as const,
      status: 'active',
      connectedAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      lastSyncedAt: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      canConnect: true, // Allow real connection
    },
    {
      id: 'demo-account-google-display',
      platform: 'google_display',
      accountName: 'Demo Google Display Account',
      accountType: 'demo_data' as const,
      status: 'active',
      connectedAt: new Date(Date.now() - 85 * 24 * 60 * 60 * 1000),
      lastSyncedAt: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      canConnect: true,
    },
    {
      id: 'demo-account-google-search',
      platform: 'google_search',
      accountName: 'Demo Google Search Account',
      accountType: 'demo_data' as const,
      status: 'active',
      connectedAt: new Date(Date.now() - 88 * 24 * 60 * 60 * 1000),
      lastSyncedAt: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      canConnect: true,
    },
    {
      id: 'demo-account-performance-max',
      platform: 'performance_max',
      accountName: 'Demo Performance Max Account',
      accountType: 'demo_data' as const,
      status: 'active',
      connectedAt: new Date(Date.now() - 80 * 24 * 60 * 60 * 1000),
      lastSyncedAt: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      canConnect: true,
    },
    {
      id: 'demo-account-tiktok',
      platform: 'tiktok',
      accountName: 'Demo TikTok Account',
      accountType: 'demo_data' as const,
      status: 'active',
      connectedAt: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
      lastSyncedAt: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
      canConnect: true,
    },
    {
      id: 'demo-account-line',
      platform: 'line',
      accountName: 'Demo LINE Ads Account',
      accountType: 'demo_data' as const,
      status: 'active',
      connectedAt: new Date(Date.now() - 80 * 24 * 60 * 60 * 1000),
      lastSyncedAt: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
      canConnect: true,
    },
  ]
}




// ============================================================================
// EXPORT ALL FUNCTIONS
// ============================================================================

export default {
  getDemoCampaignMetrics,
  getDemoChannelPerformance,
  getDemoTrendData,
  getDemoCampaignsFromClassified,
  getDemoBrokerPerformance,
  getDemoPlatformAccounts,
  getDemoMMMData,
  getDemoFunnelData,
  classifyCampaignByFunnel,
  generateRealisticTrendingValue,
  getTierROAS,
  getTierCTR,
  clearDemoDataCache,
  generateTrendingValue,
  // Demo configuration exports
  getTierConfig,
  generateCampaigns,
  DEMO_CONFIG,
  DEMO_DATE_ANCHOR,
  DEMO_DATA_RANGE_DAYS,
  // Static data loading
  USE_STATIC_DEMO_DATA,
  loadStaticDemoData,
  clearStaticDataCache,
  hasStaticDemoData,
}
